import { IconButton, Box, Button, Checkbox, FormControl, FormControlLabel, InputLabel, Select, SelectChangeEvent, TextField, css } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import alertify from 'alertifyjs';

import { Reset } from 'components/icons/components';
import { useSessionStore } from 'session/useSessionStore';
import { GeopostPopup } from 'components/geopostPopup/GeopostPopup';
import { useGetAllByLine } from 'features/seismic/components/interpretation/api/useInterpretationController';
import { useSeismicStore } from 'features/seismic/stores/useSeismicStore';
import { useGetAllAlineWeights, useExecuteAline, useGetAlineHistoric } from '../API/useAlineController';
import { AlineWeight } from '../models/IAlineWeight';
import { IRunAlineRequest } from '../models/IRunAlineRequest';
import { IInterpretation } from 'features/seismic/components/interpretation/models/interfaces/IInterpretation';
import { useLine3DNavigationStore } from 'features/seismic/stores/useLine3DNavigationStore';
import { SurveyType } from 'features/seismic/models/enums/SurveyType';
import { LineType } from 'features/seismic-3d/models/enums/LineType';
import { IAlineHistoric } from '../models/IAlineHistoric';
import { useGetSeismicAttributes } from 'features/seismic/components/attributes/api/useSeismicAttributesController';

const style = css({
    '& #select-models-label, #select-horizon-base-label, #select-horizon-fundo-label': {
        backgroundColor: '#ffffff',
        paddingLeft: '5px',
        paddingRight: '5px'
    },
    '& .red-cell': {
        color: 'red',
    },
    '& .orange-cell': {
        color: 'orange',
    },
    '& .green-cell': {
        color: 'green',
    },
});

export function AlinePopup() {
    const { t } = useTranslation(['common', 'seismic']);

    const { volumeToken } = useSeismicStore(state => ({
        volumeToken: state.volumeToken,
    }));
    const [openedAlineWorflow, setOpenedAlineWorflow] = useState(false);
    const [selectedWeight, setSelectedWeight] = useState<AlineWeight>();
    const [topHorizon, setTopHorizon] = useState<IInterpretation>();
    const [bottonHorizon, setBottonHorizon] = useState<IInterpretation>();
    const [historicOpen, setHistoricOpen] = useState<boolean>(false);
    const [rows, setRows] = useState<IAlineHistoric[]>([]);
    const [curretSeismicCheck, setCurretSeismicCheck] = useState<boolean>(false);
    const [attributesVolumeToken, setAttributesVolumeToken] = useState<string[]>([]);
    const [alineIsExecuting, setAlineIsExecuting] = useState<boolean>(false);

    const alineWeights = useGetAllAlineWeights();
    const { lineNumber, lineType } = useLine3DNavigationStore(state => ({
        lineNumber: state.lineNumber,
        lineType: state.lineType,
        setLineType: state.setLineType,
    }));
    const { surveyMetadata, attributes, setAttributeVersion } = useSeismicStore(state => ({
        surveyMetadata: state.surveyMetadata,
        attributes: state.attributes,
        setAttributeVersion: state.setAttributeVersion,
    }));

    const { data: interpretations, refetch: interpretationsRefetch } = useGetAllByLine(volumeToken, surveyMetadata?.Type === SurveyType.Seismic2D ? LineType.Line2D : lineType, lineNumber, surveyMetadata?.Domain);

    const handleChangeWeight = (event: SelectChangeEvent) => {
        const selected = alineWeights.data?.filter(weight => weight.Token === event.target.value);
        if (selected) {
            setSelectedWeight(selected[0]);
        }
    };
    const handleChangeTopHorizon = (event: SelectChangeEvent) => {
        console.log(interpretations);
        const selected = interpretations?.Interpretations.filter(interpretation => interpretation.Token === event.target.value);
        if (selected) {
            setTopHorizon(selected[0]);
        }
    };
    const handleChangeBottonHorizon = (event: SelectChangeEvent) => {
        const selected = interpretations?.Interpretations.filter(interpretation => interpretation.Token === event.target.value);
        if (selected) {
            setBottonHorizon(selected[0]);
        }
    };

    const { data: alineData, mutateAsync, isLoading } = useExecuteAline();
    const { data: historicData, isLoading: historicDataLoading, refetch: historicRefetch, isRefetching: historicDataRefetching } = useGetAlineHistoric(historicOpen);

    const runAline = async () => {
        const params: IRunAlineRequest = {
            volumeToken: volumeToken ?? '',
            topInterpretationToken: topHorizon?.Token ?? '',
            bottomInterpretationToken: bottonHorizon?.Token ?? '',
            weightToken: selectedWeight?.Token ?? '',
            lineNumber: lineNumber,
            lineOrientation: lineType
        };

        if (volumeToken && topHorizon && bottonHorizon && selectedWeight) {
            setAlineIsExecuting(true);
            alertify.alert(`ALINE workflow execution has started. It may take few minutes to be finished`);
            await mutateAsync(params);
        }
        else {
            alertify.alert(`It's necessary to fill in all inputs`);
        }
    };

    const openHistoric = () => {
        setHistoricOpen(true);
    };

    const buildIconHeadersAline = () => [
        <IconButton key='interpretation-popup-reset' color='inherit' size='small' onClick={() => { interpretationsRefetch(); setAttributeVersion() }} title={t('common:refresh')}>
            <Reset />
        </IconButton >
    ];

    const buildIconHeadersHistoric = () => [
        <IconButton key='interpretation-popup-reset' color='inherit' size='small' onClick={() => historicRefetch()} title={t('common:refresh')}>
            <Reset />
        </IconButton >
    ];

    useEffect(() => {
        if (historicData) {
            setRows(historicData);
        }
    }, [historicData]);

    useEffect(() => {
        if (attributes) {
            let atttributesVolumeToken = attributes.map(x => x.VolumeToken);

            setAttributesVolumeToken(atttributesVolumeToken);
        }
    }, [attributes]);

    useEffect(() => {
        if (alineData) {
            setAlineIsExecuting(false);

            if (alineData.Error) {
                alertify.alert('Error on ALINE processing', alineData.Message);
            }
            else {
                alertify.alert('Aline Execution Success', 'ALINE finished performing correctly. Open the attribute panel to display it.');
                setAttributeVersion();
            }
        }
    }, [alineData]);

    const columns: GridColDef[] = [
        // {
        //     field: 'UserName',
        //     headerName: 'User',
        //     flex: 1,
        //     sortable: false,
        // },
        {
            field: 'AlineStatus',
            headerName: 'Status',
            flex: 1,
            sortable: false,
            cellClassName: (params) => {
                const hasError = params.row.hasError;
                const status = params.row.AlineStatus;

                if (hasError && status === 'Finished') {
                    return 'green-cell';
                } else if (hasError) {
                    return 'red-cell';
                } else if (!hasError && status !== 'Finished') {
                    return 'orange-cell';
                }

                return '';
            },
        },
        {
            field: 'StartTime',
            headerName: 'Start Time',
            flex: 1,
            sortable: false,
        },
        {
            field: 'EndTime',
            headerName: 'End Time',
            flex: 1,
            sortable: false,
        },
        {
            field: 'WeightName',
            headerName: 'Weight',
            flex: 1,
            sortable: false,
        },
        {
            field: 'VolumeName',
            headerName: 'Volume',
            flex: 1,
            sortable: false,
            valueGetter: (params) => {
                return params.row.VolumeName || params.row.VolumeToken;
            },
        },
        {
            field: 'TopInterpretationName',
            headerName: 'Horizon Top',
            flex: 1,
            sortable: false,
            valueGetter: (params) => {
                return params.row.TopInterpretationName || params.row.TopInterpretationToken;
            },
        },
        {
            field: 'BottomInterpretationName',
            headerName: 'Horizon Bottom',
            flex: 1,
            sortable: false,
            valueGetter: (params) => {
                return params.row.BottomInterpretationName || params.row.BottomInterpretationToken;
            },
        },
        {
            field: 'LastMessage',
            headerName: 'Last Message',
            flex: 1,
            sortable: false,
        },
    ];

    return <Box>

        <Button sx={{ color: 'white', borderColor: 'white' }} variant='outlined' onClick={() => setOpenedAlineWorflow(true)}>ALINE</Button>
        <div>
            <GeopostPopup
                temporaryIgnoreFlexInContainer={false}
                open={openedAlineWorflow}
                title={'ALINE'}
                onChangeOpen={value => setOpenedAlineWorflow(value)}
                overlay={false}
                height={430}
                width={400}
                iconHeaders={buildIconHeadersAline()}
            >
                <Box css={style} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <FormControl sx={{ m: 1, minWidth: 160 }}>
                        <InputLabel id='select-models-label'>{'Models'}</InputLabel>

                        <Select
                            native
                            id='select-aline-model'
                            labelId='select-models-label'
                            onChange={handleChangeWeight}
                            value={selectedWeight?.Token}
                        >
                            <option value={0}>Models</option>
                            {alineWeights.data?.map(weight => <option key={weight.Token} value={weight.Token}>{weight.Name}</option>)}
                        </Select>
                    </FormControl>
                    <FormControl sx={{ m: 1, minWidth: 160 }}>
                        <TextField
                            style={{ textAlign: 'left' }}
                            multiline
                            rows={3}
                            label={'Description'}
                            value={selectedWeight?.Description ?? ''}
                        />
                    </FormControl>
                    <FormControl sx={{ m: 1, minWidth: 160 }}>
                        <InputLabel id='select-horizon-base-label'>{'Horizon Top'}</InputLabel>
                        <Select
                            native
                            labelId='select-horizon-base-label'
                            id='select-horizon-base'
                            value={topHorizon?.Token}
                            onChange={handleChangeTopHorizon}
                        >
                            <option value={0}>Select</option>
                            {interpretations?.Interpretations.map(interpretation => <option key={interpretation.Token} value={interpretation.Token}>{interpretation.Name}</option>)}

                        </Select>
                    </FormControl>
                    <FormControl sx={{ m: 1, minWidth: 160 }}>
                        <InputLabel id='select-horizon-fundo-label'>{'Horizon Bottom'}</InputLabel>
                        <Select
                            native
                            labelId='select-horizon-fundo-label'
                            id='select-horizon-fundo'
                            value={bottonHorizon?.Token}
                            onChange={handleChangeBottonHorizon}
                        >
                            <option value={0}>Select</option>
                            {interpretations?.Interpretations.map(interpretation => <option key={interpretation.Token} value={interpretation.Token}>{interpretation.Name}</option>)}
                        </Select>
                    </FormControl>
                    <FormControl sx={{ m: 1, minWidth: 160, direction: 'row' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                            <Button disabled={alineIsExecuting} fullWidth variant='outlined' onClick={runAline}>start</Button>
                            <Button fullWidth variant='outlined' onClick={openHistoric}>Historic</Button>
                        </Box>
                    </FormControl>
                </Box>
            </GeopostPopup>
            <GeopostPopup
                temporaryIgnoreFlexInContainer={false}
                open={historicOpen}
                title={'ALINE workflows historic'}
                onChangeOpen={value => setHistoricOpen(value)}
                overlay={false}
                height={400}
                width={1400}
                iconHeaders={buildIconHeadersHistoric()}
            >
                <Box css={style} sx={{ display: 'flex', width: 'calc(100% - 32px)', flexDirection: 'column', padding: '16px' }}>
                    <FormControlLabel control={
                        <Checkbox
                            checked={curretSeismicCheck}
                            onChange={() => setCurretSeismicCheck(!curretSeismicCheck)} />}
                    label={'Show only of the current seismic'}
                    />
                    <DataGrid
                        getRowId={(row) => row.Token}
                        rows={curretSeismicCheck ? rows.filter(x => attributesVolumeToken.includes(x.VolumeToken)) : rows}
                        columns={columns}
                        loading={historicDataLoading || historicDataRefetching}
                        autoHeight={rows ? (rows.length > 0 ? false : true) : true}
                        initialState={{
                            pagination: {
                                paginationModel: {
                                    pageSize: 10,
                                },
                            },
                        }}
                        sx={{ fontSize: '0.75rem' }}
                        pageSizeOptions={[10]}
                    />
                </Box>
            </GeopostPopup>
        </div>
    </Box>;
}